import { axiosAnonymousInstance } from "configurations/axiosConfig";
import { PolicyUploadConfiguration, TitleSearchUploadConfiguration, UWAUploadConfiguration} from "entities/UIModel";
import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

interface FeatureConfiguration {
  enableSignatures: boolean;
  enableReportPay: boolean;
  enableTitleSearch: boolean;
  enableUwa: boolean;
  enableUndoVoidJacket: boolean;
  enableHelpCenter: boolean;
  enableSpyMode: boolean;
  inMaintenanceMode: boolean;
  maintenanceMessage: string;
}

interface GeneralConfiguration {
  saDomain: string;
  userInputDebounceTimeMs: number;
  filesSearchTimeoutMs: number;
  filesSearchDefaultLookupDays: number;
  policyUploadConfiguration?: PolicyUploadConfiguration;
  titleSearchUploadConfiguration?: TitleSearchUploadConfiguration;
  titleSearchRequireLoanAmountProductList?: string[];
  uwaUploadConfiguration?: UWAUploadConfiguration;
  supportEmail :string;
  supportPhone :string;
  mainDomain: string;
  adminDomain: string;
  pspDomain: string;
}

interface State {
  featureConfig: FeatureConfiguration;
  generalConfig: GeneralConfiguration;
}

type Props = {
  children?: ReactNode;
};

const initialContext: State = {
  featureConfig: {
    enableSignatures: true,
    enableReportPay: true,
    enableTitleSearch: false,
    enableUwa: false,
    enableUndoVoidJacket: false,
    enableHelpCenter: false,
    enableSpyMode: false,
    inMaintenanceMode: false,
    maintenanceMessage: "",
  },
  generalConfig: {
    saDomain: "",
    userInputDebounceTimeMs: 750,
    filesSearchTimeoutMs: 90000,
    filesSearchDefaultLookupDays: 10,
    policyUploadConfiguration: undefined,
    titleSearchRequireLoanAmountProductList: undefined,
    supportEmail :"",
    supportPhone :"",
    mainDomain: "",
    adminDomain: "",
    pspDomain: "",
  },
};

const getFeatureConfig = async () => {
  const { data } = await axiosAnonymousInstance.get("/configuration/features");
  const retval: FeatureConfiguration = {
    enableSignatures: data.Signatures.Enabled,
    enableReportPay: data.ReportPay.Enabled,
    enableTitleSearch: data.TitleSearch.Enabled,
    enableUwa: data.UWA.Enabled,
    enableUndoVoidJacket: data.Jacket.UndoVoidEnabled,
    enableHelpCenter: data.HelpCenterEnabled,
    enableSpyMode: data.SpyMode,
    inMaintenanceMode: data.InMaintenanceMode,
    maintenanceMessage: data.MaintenanceMessage,
  };
  return retval;
};

const getGeneralConfig = async () => {
  const { data } = await axiosAnonymousInstance.get("/configuration/generalConfig");
  const retval: GeneralConfiguration = {
    saDomain: data.SADomain,
    userInputDebounceTimeMs: Number(data.UserInputDebounceTimeMs),
    filesSearchTimeoutMs: Number(data.FilesSearchTimeoutMs),
    filesSearchDefaultLookupDays: Number(data.FilesSearchDefaultLookupDays),
    policyUploadConfiguration: data.PolicyUploadConfiguration ? JSON.parse(data.PolicyUploadConfiguration) : undefined,
    titleSearchUploadConfiguration: data.TitleSearchUploadConfiguration ? JSON.parse(data.TitleSearchUploadConfiguration) : undefined,
    titleSearchRequireLoanAmountProductList: data.TitleSearchRequireLoanAmountProductList ? JSON.parse(data.TitleSearchRequireLoanAmountProductList) : undefined,
    uwaUploadConfiguration: data.UWAUploadConfiguration ? JSON.parse(data.UWAUploadConfiguration) : undefined,
    supportEmail :data.SupportEmail || "",
    supportPhone :data.SupportPhone || "",
    mainDomain: data.MainDomain,
    adminDomain: data.AdminDomain,
    pspDomain: data.PSPDomain,
  };
  return retval;
};

const ConfigContext = createContext<State>(initialContext);

const ConfigContextProvider = ({ children }: Props) => {
  const [state, setState] = useState<State>(initialContext);

  useEffect(() => {
    const fetchConfigs = async () => {
      const featureConfigData = await getFeatureConfig();
      const generalConfigData = await getGeneralConfig();

      setState({
        featureConfig: featureConfigData,
        generalConfig: generalConfigData,
      });
    };

    fetchConfigs();
  }, []);

  const contextValue = useMemo(() => state, [state]);

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfigContext = () => {
  const context = React.useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfigContext must be used within a <ConfigContextProvider>.");
  }
  return context;
};

export default ConfigContextProvider;
